import { flash } from "@xola/ui-kit";
import { AxiosError } from "axios";
import { get } from "lodash";
import React, { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { BaseSchedule, Guide } from "types";
import { api } from "../../../api/api";
import { useSeller } from "../../../hooks/useAccount";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { useVisibility } from "../../../hooks/useVisibility";
import { BaseScheduleInput } from "../../BaseSchedule/BaseScheduleInput";
import { ConfirmDeleteGuideScheduleModal } from "./ConfirmDeleteGuideScheduleModal";
import { ConfirmEditGuideScheduleModal } from "./ConfirmEditGuideScheduleModal";

interface GuideScheduleProps {
    guide: Guide;
}

export interface DeleteSchedulePayload {
    schedule: BaseSchedule;
    forceDelete?: boolean;
}

enum ScheduleEditModalTypes {
    EDIT = "edit",
    CREATE = "create",
}

interface UpsertSchedulePayload {
    schedule: BaseSchedule;
    force?: boolean;
}

interface ScheduleError {
    field: {
        schedule: { reason: string; message: string };
    };
}

export const GuideSchedule = React.memo(({ guide }: GuideScheduleProps) => {
    const seller = useSeller();
    const invalidate = useInvalidate();
    const [showWarning, { toggle: toggleShowWarning }] = useVisibility(false);
    const [conflictingEventIds, setConflictingEventIds] = useState<ID[] | null>();
    const [conflictingEventForEditIds, setConflictingEventForEditIds] = useState<ID[] | null>();
    const [deletingSchedule, setDeletingSchedule] = useState<BaseSchedule | null>();
    const [scheduleErrorMessage, setScheduleErrorMessage] = useState<string | null>();
    const [errorScheduleUpdateType, setErrorScheduleUpdateType] = useState<
        ScheduleEditModalTypes.EDIT | ScheduleEditModalTypes.CREATE | null
    >(null);

    const [scheduleForConfirm, setScheduleForConfirm] = useState<BaseSchedule | null>();

    const handleOnSuccess = () => {
        invalidate("guides", guide.id);
        setScheduleErrorMessage(null);
        setErrorScheduleUpdateType(null);
        handleCloseWarning();
    };

    const handleOnError = (error: AxiosError<ScheduleError>, payload: UpsertSchedulePayload) => {
        const errorStatus = get(error, "response.status");
        const reason = get(error, "response.data.field.schedule.reason");
        const message = get(error, "response.data.field.schedule.message");
        const conflictingData = get(error, "response.data.field.schedule.conflictingData");

        if (
            reason === "cannot_modify_schedule" ||
            reason === "cannot_create_unavailable_schedule" ||
            reason === "cannot_create_schedule"
        ) {
            if (reason === "cannot_create_unavailable_schedule" || reason === "cannot_create_schedule") {
                setErrorScheduleUpdateType(ScheduleEditModalTypes.CREATE);
            }

            if (reason === "cannot_modify_schedule") {
                setErrorScheduleUpdateType(ScheduleEditModalTypes.EDIT);
            }

            if (errorStatus === 400) {
                flash.danger(message);
                setScheduleErrorMessage(message);
                return;
            }

            if (errorStatus === 409) {
                setScheduleForConfirm(payload.schedule);
                setConflictingEventForEditIds(conflictingData);
                toggleShowWarning();
            }
        }
    };

    const { mutateAsync: upsertSchedule } = useMutation(
        async ({ schedule, force }: UpsertSchedulePayload) => {
            const body = force ? { ...schedule, force } : { ...schedule };
            await (schedule.id
                ? api.put(`/sellers/${seller.id}/guides/${guide.id}/schedules/${schedule.id}`, body)
                : api.post(`/sellers/${seller.id}/guides/${guide.id}/schedules`, body));
        },
        {
            onSuccess: handleOnSuccess,
            onError: handleOnError,
        },
    );

    const { mutateAsync: deleteSchedule, error: deleteError } = useMutation(
        async ({ schedule, forceDelete = false }: DeleteSchedulePayload) => {
            try {
                const config = forceDelete
                    ? {
                          data: "force=true",
                          headers: { "content-type": "application/x-www-form-urlencoded; charset=UTF-8" },
                      }
                    : undefined;
                await api.delete(`/sellers/${seller.id}/guides/${guide.id}/schedules/${schedule.id}`, config);
                setConflictingEventIds(null);
                setDeletingSchedule(null);
                invalidate("guides", guide.id);
            } catch (error) {
                setDeletingSchedule(forceDelete ? null : schedule);
                throw error;
            }
        },
    );

    const handleUpsertSchedule = (schedule: BaseSchedule) => {
        upsertSchedule({ schedule });
    };

    const handleCloseWarning = () => {
        toggleShowWarning();
        setScheduleForConfirm(null);
        setConflictingEventForEditIds(null);
    };

    const handleOnConfirmGuideScheduleModal = async (schedule: BaseSchedule) => {
        upsertSchedule({ schedule, force: true });
    };

    useEffect(() => {
        const errorData = (deleteError as AxiosError)?.response?.data as any;
        if (errorData?.field?.schedule?.reason === "cannot_delete_schedule") {
            setConflictingEventIds(errorData.field.schedule.conflictingData);
        }
    }, [deleteError]);

    return (
        <>
            <BaseScheduleInput
                schedules={guide.schedules}
                description="Specifying a schedule lets managers know on what days and times this guide is available."
                errorMessage={scheduleErrorMessage}
                errorScheduleUpdateType={errorScheduleUpdateType}
                onUpsertSchedule={handleUpsertSchedule}
                onDeleteSchedule={async (schedule) => deleteSchedule({ schedule })}
            />

            <ConfirmDeleteGuideScheduleModal
                events={conflictingEventIds ?? undefined}
                guide={guide}
                schedule={deletingSchedule ?? undefined}
                onConfirm={async (schedule) => deleteSchedule({ schedule, forceDelete: true })}
                onClose={() => {
                    setConflictingEventIds(null);
                }}
            />

            <ConfirmEditGuideScheduleModal
                isOpen={showWarning}
                events={conflictingEventForEditIds ?? undefined}
                guide={guide}
                modalModificationType={errorScheduleUpdateType}
                schedule={scheduleForConfirm ?? undefined}
                onConfirm={handleOnConfirmGuideScheduleModal}
                onClose={handleCloseWarning}
            />
        </>
    );
});
